import React, {useEffect, useState} from 'react'
import {
  getConfigurationByName,
  updateConfigurationByConfigID,
  uploadMedia,
} from '../../../../../../API/api-endpoint'
import ToastUtils, {ErrorToastUtils} from '../../../../../../utils/ToastUtils'
import {getImageBaseUrl} from '../../../../../../utils/Utils'
import {Modal, Button, Table, Form} from 'react-bootstrap'
import {request} from '../../../../../../middleware/auth-intercepter'
import {API_TYPE} from '../../../../../../utils/Utils'

const DynamicPaymentGatewayPlugin = () => {
  const [configID, setConfigId] = useState<number | null>(null)
  const [gateways, setGateways] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [currentGateway, setCurrentGateway] = useState<any>({
    id: '',
    name: '',
    image: '',
    paymentVia: '',
  })
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [statuses, setStatuses] = useState<{[key: string]: boolean | null}>({})
  const [iosStatuses, setIosStatuses] = useState<{[key: string]: boolean | null}>({})
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  useEffect(() => {
    fetchGateways()
  }, [])

  const fetchGateways = async () => {
    setLoading(true)
    const result = await getConfigurationByName('dynamic_payment_gateways')
    if (result?.success && result?.data) {
      const parsedData = JSON.parse(result.data.values)
      setConfigId(result.data.id)
      setGateways(parsedData || [])
      // Fetch statuses for each gateway
      fetchStatuses(parsedData || [])
    } else {
      setGateways([])
    }
    setLoading(false)
  }

  const fetchStatuses = async (gatewayList: any[]) => {
    const statusMap: {[key: string]: boolean | null} = {}
    const iosStatusMap: {[key: string]: boolean | null} = {}
    for (const gw of gatewayList) {
      if (gw.paymentVia) {
        const res = await getConfigurationByName(gw.paymentVia)
        if (res?.success && res?.data) {
          try {
            const vals = JSON.parse(res.data.values)
            statusMap[gw.paymentVia] = vals.isEnabled ?? vals.status ?? false
            iosStatusMap[gw.paymentVia] = vals.isEnabledIos ?? false
          } catch (e) {
            statusMap[gw.paymentVia] = false
            iosStatusMap[gw.paymentVia] = false
          }
        } else {
          statusMap[gw.paymentVia] = null // Not found
          iosStatusMap[gw.paymentVia] = null
        }
      }
    }
    setStatuses(statusMap)
    setIosStatuses(iosStatusMap)
  }

  const handleSaveList = async (newList: any[]) => {
    if (configID === null) {
      // Create new config
      const response = await request(
        '/masters/configurations/create',
        API_TYPE.POST,
        {
          type: 'dynamic_payment_gateways',
          category: 'payment',
          status: true,
          values: JSON.stringify(newList),
        },
        true,
        false
      )
      if (response?.success) {
        ToastUtils({type: 'success', message: 'Gateways saved successfully'})
        fetchGateways()
      } else {
        ErrorToastUtils()
      }
    } else {
      const result = await updateConfigurationByConfigID(configID, newList, null)
      if (result.success) {
        ToastUtils({type: 'success', message: 'Gateways saved successfully'})
        fetchGateways()
      } else {
        ErrorToastUtils()
      }
    }
  }

  const handleToggleStatus = async (paymentVia: string, currentStatus: boolean) => {
    const res = await getConfigurationByName(paymentVia)
    if (res?.success && res?.data) {
      try {
        const vals = JSON.parse(res.data.values)
        vals.isEnabled = !currentStatus
        // Also update 'status' if it's used instead of 'isEnabled'
        if (vals.status !== undefined) vals.status = !currentStatus
        
        const result = await updateConfigurationByConfigID(res.data.id, vals, null)
        if (result.success) {
          ToastUtils({type: 'success', message: 'Status updated'})
          setStatuses({...statuses, [paymentVia]: !currentStatus})
        } else {
          ErrorToastUtils()
        }
      } catch (e) {
        ErrorToastUtils()
      }
    }
  }

  const handleToggleIosStatus = async (paymentVia: string, currentIosStatus: boolean) => {
    const res = await getConfigurationByName(paymentVia)
    if (res?.success && res?.data) {
      try {
        const vals = JSON.parse(res.data.values)
        vals.isEnabledIos = !currentIosStatus
        
        const result = await updateConfigurationByConfigID(res.data.id, vals, null)
        if (result.success) {
          ToastUtils({type: 'success', message: 'iOS Status updated'})
          setIosStatuses({...iosStatuses, [paymentVia]: !currentIosStatus})
        } else {
          ErrorToastUtils()
        }
      } catch (e) {
        ErrorToastUtils()
      }
    }
  }

  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0]
    if (!file) return
    setLoading(true)
    const result = await uploadMedia(file)
    setLoading(false)
    if (result.success) {
      const path = result.data.path
      setCurrentGateway({...currentGateway, image: path})
      ToastUtils({type: 'success', message: 'Image uploaded'})
    } else {
      ErrorToastUtils()
    }
  }

  const openAddModal = () => {
    setCurrentGateway({id: Date.now().toString(), name: '', image: '', paymentVia: ''})
    setEditingIndex(null)
    setShowModal(true)
  }

  const openEditModal = (index: number) => {
    setCurrentGateway({...gateways[index]})
    setEditingIndex(index)
    setShowModal(true)
  }

  const saveGateway = () => {
    if (!currentGateway.name || !currentGateway.paymentVia) {
      ToastUtils({type: 'error', message: 'Name and PaymentVia are required'})
      return
    }
    const newList = [...gateways]
    if (editingIndex !== null) {
      newList[editingIndex] = currentGateway
    } else {
      newList.push(currentGateway)
    }
    setGateways(newList)
    handleSaveList(newList)
    setShowModal(false)
  }

  const deleteGateway = (index: number) => {
    if (window.confirm('Are you sure you want to delete this gateway?')) {
      const newList = gateways.filter((_, i) => i !== index)
      setGateways(newList)
      handleSaveList(newList)
    }
  }

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault() // Necessary to allow dropping
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newList = [...gateways]
    const itemToMove = newList[draggedIndex]
    newList.splice(draggedIndex, 1)
    newList.splice(index, 0, itemToMove)

    setGateways(newList)
    setDraggedIndex(null)
    handleSaveList(newList)
  }

  return (
    <div className='card'>
      <div className='card-header pt-7 border-0'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-gray-800'>Dynamic Payment Gateways</span>
          <span className='text-gray-400 mt-1 fw-semibold fs-6'>Manage custom payment gateway list</span>
        </h3>
        <div className='card-toolbar'>
          <button className='btn btn-sm btn-primary' onClick={openAddModal}>
            Add Gateway
          </button>
        </div>
      </div>
      <div className='card-body pt-5'>
        <div className='table-responsive'>
          <Table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bold text-muted bg-light'>
                <th className='ps-4 min-w-100px rounded-start'>Image</th>
                <th className='min-w-150px'>Name</th>
                <th className='min-w-150px'>PaymentVia</th>
                <th className='min-w-100px text-center'>Status</th>
                <th className='min-w-100px text-center'>Status (iOS)</th>
                <th className='min-w-100px text-end rounded-end'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {gateways.map((item, index) => (
                <tr
                  key={item.id || index}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{cursor: 'move', backgroundColor: draggedIndex === index ? '#f5f5f5' : 'inherit'}}
                  className={draggedIndex === index ? 'opacity-50' : ''}
                >
                  <td className='ps-4'>
                    <div className='d-flex align-items-center'>
                      <i className='fa-solid fa-grip-vertical text-muted me-4 fs-4' title='Drag to reorder'></i>
                      {item.image ? (
                        <div className='symbol symbol-45px'>
                          <img
                            src={item.image.startsWith('http') ? item.image : `${getImageBaseUrl()}/${item.image}`}
                            alt={item.name}
                            style={{objectFit: 'contain'}}
                          />
                        </div>
                      ) : (
                        <div className='symbol symbol-45px bg-light rounded d-flex align-items-center justify-content-center'>
                          <i className='fa-solid fa-image text-muted'></i>
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className='text-gray-800 fw-bold d-block fs-6'>{item.name}</span>
                  </td>
                  <td>
                    <code className='text-primary fw-bold'>{item.paymentVia}</code>
                  </td>
                  <td className='text-center'>
                    {statuses[item.paymentVia] === null ? (
                      <span className='badge badge-light-warning fw-bold'>Not Configured</span>
                    ) : (
                      <div className='form-check form-switch d-flex justify-content-center'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          role='switch'
                          checked={!!statuses[item.paymentVia]}
                          onChange={() => handleToggleStatus(item.paymentVia, !!statuses[item.paymentVia])}
                        />
                      </div>
                    )}
                  </td>
                  <td className='text-center'>
                    {iosStatuses[item.paymentVia] === null ? (
                      <span className='badge badge-light-warning fw-bold'>Not Configured</span>
                    ) : (
                      <div className='form-check form-switch d-flex justify-content-center'>
                        <input
                          className='form-check-input'
                          type='checkbox'
                          role='switch'
                          checked={!!iosStatuses[item.paymentVia]}
                          onChange={() => handleToggleIosStatus(item.paymentVia, !!iosStatuses[item.paymentVia])}
                        />
                      </div>
                    )}
                  </td>
                  <td className='text-end'>
                    <button className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1' onClick={() => openEditModal(index)}>
                      <i className='fa-solid fa-pencil'></i>
                    </button>
                    <button className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' onClick={() => deleteGateway(index)}>
                      <i className='fa-solid fa-trash'></i>
                    </button>
                  </td>
                </tr>
              ))}
              {gateways.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className='text-center text-muted py-10'>
                    No gateways added yet. Click "Add Gateway" to start.
                  </td>
                </tr>
              )}
              {loading && (
                 <tr>
                 <td colSpan={6} className='text-center text-muted py-10'>
                   Loading...
                 </td>
               </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className='fw-bold'>{editingIndex !== null ? 'Edit Gateway' : 'Add New Gateway'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='px-10 py-10'>
          <Form>
            <Form.Group className='mb-7'>
              <Form.Label className='fw-bold'>Gateway Name</Form.Label>
              <Form.Control
                type='text'
                className='form-control-solid'
                value={currentGateway.name}
                onChange={(e) => setCurrentGateway({...currentGateway, name: e.target.value})}
                placeholder='e.g. My Custom Gateway'
              />
            </Form.Group>
            <Form.Group className='mb-7'>
              <Form.Label className='fw-bold'>PaymentVia (Key name in Config)</Form.Label>
              <Form.Control
                type='text'
                className='form-control-solid'
                value={currentGateway.paymentVia}
                onChange={(e) => setCurrentGateway({...currentGateway, paymentVia: e.target.value})}
                placeholder='e.g. custom_pay'
              />
              <Form.Text className='text-muted fs-7'>
                This must match exactly the <strong>type</strong> used in system configurations.
              </Form.Text>
            </Form.Group>
            <Form.Group className='mb-7'>
              <Form.Label className='fw-bold d-block'>Gateway Icon</Form.Label>
              <div className='d-flex align-items-center gap-5 mt-2'>
                <div className='symbol symbol-100px symbol-lg-150px border border-dashed border-primary bg-light d-flex align-items-center justify-content-center'>
                  {currentGateway.image ? (
                    <img
                      src={currentGateway.image.startsWith('http') ? currentGateway.image : `${getImageBaseUrl()}/${currentGateway.image}`}
                      alt='preview'
                      className='w-100 h-100'
                      style={{objectFit: 'contain'}}
                    />
                  ) : (
                    <i className='fa-solid fa-image fs-3x text-muted'></i>
                  )}
                </div>
                <div className='flex-grow-1'>
                  <Form.Control 
                    type='file' 
                    className='form-control-solid' 
                    onChange={handleFileUpload} 
                    accept='image/*' 
                  />
                  <div className='text-muted fs-7 mt-2'>Allowed types: png, jpg, jpeg, webp.</div>
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className='border-0 pt-0 px-10 pb-10'>
          <Button variant='light' onClick={() => setShowModal(false)}>
            Discard
          </Button>
          <Button variant='primary' onClick={saveGateway} disabled={loading}>
            {loading ? 'Wait...' : (editingIndex !== null ? 'Update Gateway' : 'Create Gateway')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default DynamicPaymentGatewayPlugin
